/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/translationHelpers.ts
 🗓️ Created: 2025-01-22
 🧩 Description:
    Helper functions for translating feature code names.
────────────────────────────────────────────────────────────────*/

import type { FeatureCode } from '../types';

/**
 * Generate translation key for feature code name
 */
export const getFeatureCodeTranslationKey = (featureCode: FeatureCode): string => {
  // Normalize category name: lowercase, replace spaces with underscores, preserve &, handle special chars
  const categoryKey = (featureCode.category_name || featureCode.category?.name || '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_&]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  // Normalize feature code name: lowercase, replace spaces with underscores, handle special chars
  let featureKey = (featureCode.name || '')
    .toLowerCase()
    // Handle parentheses first: (self) -> _self, (Barge Mode) -> _barge_mode
    .replace(/\(([^)]+)\)/g, (match, content) => {
      const normalized = content.trim().toLowerCase().replace(/\s+/g, '_');
      return `_${normalized}`;
    })
    // Replace slashes with underscores
    .replace(/\//g, '_')
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Replace other special chars with underscores
    .replace(/[^a-z0-9_]/g, '_')
    // Collapse multiple underscores
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_|_$/g, '');

  return `featureCodes.${categoryKey}.${featureKey}`;
};

/**
 * Get translated feature code name
 */
export const getTranslatedFeatureCodeName = (featureCode: FeatureCode, t: (key: string) => string): string => {
  // Normalize category name
  const categoryKey = (featureCode.category_name || featureCode.category?.name || '')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_&]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  // Clean the feature code name - remove category prefix if present
  let cleanName = featureCode.name.trim();

  // Check if name contains a dot (likely a category prefix)
  if (cleanName.includes('.')) {
    const parts = cleanName.split('.');
    const prefix = parts[0].toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_&]/g, '');
    if (prefix === categoryKey || prefix.replace(/_features$/, '') === categoryKey.replace(/_features$/, '')) {
      cleanName = parts.slice(1).join('.');
    }
  }

  // Try multiple approaches to find the translation
  const attempts = [
    cleanName.toLowerCase().trim(),
    getFeatureCodeTranslationKey(featureCode).split('.').pop() || '',
    featureCode.name.toLowerCase().trim(),
  ];

  for (const attempt of attempts) {
    if (!attempt) continue;

    const translationKey = `featureCodes.${categoryKey}.${attempt}`;
    let translated = t(translationKey);

    // Check if translation was found
    if (translated !== translationKey && translated && !translated.startsWith('featureCodes.')) {
      return translated;
    }
  }

  // If still not found, return original name
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[FeatureCodes] Translation not found:`, {
      attempts: attempts,
      feature: featureCode.name,
      cleanName: cleanName,
      category: featureCode.category_name,
      categoryKey: categoryKey,
    });
  }
  return featureCode.name;
};

